export type ShortcutScope =
  | 'global'
  | 'canvas'
  | 'canvas-selection'
  | 'text-editing'
  | 'filmstrip';

export interface KeyChord {
  key: string;
  /** metaKey || ctrlKey (Cmd on mac, Ctrl on win). */
  mod?: boolean;
  /** ctrlKey specifically, metaKey must be false (Ctrl+M alongside Cmd chords on mac). */
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** Omit = both platforms; 'win' covers every non-mac platform. */
  platform?: 'mac' | 'win';
}

export interface KeyEventLike {
  key: string;
  code?: string;
  location?: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  repeat: boolean;
  isComposing?: boolean;
  altGraphKey?: boolean;
  preventDefault?: () => void;
  stopPropagation?: () => void;
}

export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iP(hone|ad|od)/.test(navigator.platform || navigator.userAgent);
}

// Self-contained on purpose: the iframe runtime embeds this function's source
// via `eventMatchesChord.toString()` (same pattern as makeElementId in
// slide-frame.tsx), so it must not close over anything in this module.
export function eventMatchesChord(
  event: {
    key: string;
    metaKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
  },
  chord: {
    key: string;
    mod?: boolean;
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
    platform?: 'mac' | 'win';
  },
  isMac: boolean
): boolean {
  if (chord.platform === 'mac' && !isMac) return false;
  if (chord.platform === 'win' && isMac) return false;
  const shiftedPairs: Record<string, string> = {
    '+': '=',
    _: '-',
    '>': '.',
    '<': ',',
    '?': '/',
  };
  const normalize = (key: string) => (shiftedPairs[key] ?? key).toLowerCase();
  if (normalize(event.key) !== normalize(chord.key)) return false;
  const mod = event.metaKey || event.ctrlKey;
  if (chord.ctrl === true) {
    if (!event.ctrlKey || event.metaKey) return false;
  } else if (chord.mod === true) {
    if (!mod) return false;
  } else if (mod) {
    return false;
  }
  if ((chord.shift === true) !== event.shiftKey) return false;
  if ((chord.alt === true) !== event.altKey) return false;
  return true;
}

/**
 * Chords the iframe runtime intercepts (preventDefault synchronously) and
 * forwards to the parent registry while NOT text-editing. Injected into the
 * iframe document as JSON at build time — keep this pure data.
 */
export const IFRAME_FORWARDED_CHORDS: KeyChord[] = [
  // Clipboard + duplicate
  { key: 'c', mod: true },
  { key: 'x', mod: true },
  { key: 'v', mod: true },
  { key: 'd', mod: true },
  // History
  { key: 'z', mod: true },
  { key: 'z', mod: true, shift: true },
  // Delete
  { key: 'Backspace' },
  { key: 'Delete' },
  // Nudge (plain), resize (Shift), coarse nudge (mod+Shift), rotate (Alt)
  { key: 'ArrowLeft' },
  { key: 'ArrowLeft', shift: true },
  { key: 'ArrowLeft', mod: true, shift: true },
  { key: 'ArrowLeft', alt: true },
  { key: 'ArrowRight' },
  { key: 'ArrowRight', shift: true },
  { key: 'ArrowRight', mod: true, shift: true },
  { key: 'ArrowRight', alt: true },
  { key: 'ArrowUp' },
  { key: 'ArrowUp', shift: true },
  { key: 'ArrowUp', mod: true, shift: true },
  { key: 'ArrowDown' },
  { key: 'ArrowDown', shift: true },
  { key: 'ArrowDown', mod: true, shift: true },
  // Enter text edit mode
  { key: 'Enter' },
  { key: 'F2' },
  // Zoom
  { key: '=', mod: true },
  { key: '=', mod: true, shift: true },
  { key: '-', mod: true },
  { key: '-', mod: true, shift: true },
  { key: '0', mod: true },
  // Slide navigation + creation
  { key: 'PageUp' },
  { key: 'PageDown' },
  { key: 'Home' },
  { key: 'End' },
  { key: 'n', mod: true, shift: true },
  { key: 'm', ctrl: true },
  { key: 'd', mod: true, shift: true },
  { key: 'y', mod: true },
  // Z-order
  { key: 'f', mod: true, shift: true },
  { key: 'b', mod: true, shift: true },
  // Group / ungroup
  { key: 'g', mod: true },
  { key: 'g', mod: true, shift: true },
  // Save + help
  { key: 's', mod: true },
  { key: '/', mod: true },
  { key: '/', shift: true },
  // New chat
  { key: 'o', mod: true, shift: true },
  // Find / replace (Cmd+H is macOS hide-window, hence the Office-Mac ⌘⇧H)
  { key: 'f', mod: true },
  { key: 'h', ctrl: true, platform: 'win' },
  { key: 'h', mod: true, shift: true, platform: 'mac' },
  { key: 'F3' },
  { key: 'F3', shift: true },
  // Single-key inserts (mac; Windows uses Alt-digit QAT sequences, which are
  // not intercepted inside the iframe)
  { key: 't', platform: 'mac' },
  { key: 'r', platform: 'mac' },
  { key: 'o', platform: 'mac' },
  { key: 'l', platform: 'mac' },
  { key: 'a', platform: 'mac' },
  // Comment mode toggle
  { key: 'c', platform: 'mac' },
];

/**
 * Chords intercepted and forwarded WHILE a TinyMCE editor owns the caret.
 * Everything else stays with TinyMCE (native Cmd+B/I/U, its own undo).
 */
export const IFRAME_FORWARDED_TEXT_CHORDS: KeyChord[] = [
  // Paragraph alignment
  { key: 'l', mod: true },
  { key: 'e', mod: true },
  { key: 'r', mod: true },
  // Font size step
  { key: '.', mod: true, shift: true },
  { key: ',', mod: true, shift: true },
  // Superscript / subscript
  { key: '=', mod: true, shift: true },
  { key: '=', mod: true },
  // Save
  { key: 's', mod: true },
  // New chat
  { key: 'o', mod: true, shift: true },
  // Find / replace (opening the find bar moves focus out and ends the text
  // session, so these are safe to take from TinyMCE)
  { key: 'f', mod: true },
  { key: 'h', ctrl: true, platform: 'win' },
  { key: 'h', mod: true, shift: true, platform: 'mac' },
  { key: 'F3' },
  { key: 'F3', shift: true },
  // Group chords stay intercepted mid-edit so the browser's native
  // find-again (Safari Cmd+G) never fires; grouping itself is inert while
  // text editing (canvas-selection scope is inactive).
  { key: 'g', mod: true },
  { key: 'g', mod: true, shift: true },
];

const KEY_LABELS: Record<string, string> = {
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  escape: 'Esc',
  enter: 'Enter',
  backspace: 'Backspace',
  delete: 'Del',
  pageup: 'Page Up',
  pagedown: 'Page Down',
  home: 'Home',
  end: 'End',
  ' ': 'Space',
};

export function formatChord(chord: KeyChord, isMac: boolean): string {
  const lower = chord.key.toLowerCase();
  const label =
    KEY_LABELS[lower] ?? (lower.length === 1 ? lower.toUpperCase() : chord.key);
  if (isMac) {
    return [
      chord.ctrl ? '⌃' : '',
      chord.alt ? '⌥' : '',
      chord.shift ? '⇧' : '',
      chord.mod ? '⌘' : '',
      label,
    ].join('');
  }
  const parts: string[] = [];
  if (chord.mod || chord.ctrl) parts.push('Ctrl');
  if (chord.alt) parts.push('Alt');
  if (chord.shift) parts.push('Shift');
  parts.push(label);
  return parts.join('+');
}
